// SnapshotEngine — Layer 4 frontend orchestrator.
//
// Captures webcam frames during the coding test and POSTs them to
// /api/cheating/snapshot. Two triggers:
//
//   1. SCHEDULED — every N seconds (admin-tunable via
//      `snapshots.scheduled` threshold `interval_seconds`). Fixed cadence
//      so we know what's going on even when the candidate isn't doing
//      anything that fires Layer 3.
//
//   2. EVENT-TRIGGERED — listens for a custom DOM event from the Layer 3
//      detectors. Whenever a MediaPipe detector fires anything more
//      serious than 'info', a fresh snapshot is captured and POSTed so
//      the recruiter has a frame of the exact moment for the dashboard.
//
// Capture source: reuses the existing iCode webcam stream (passed in as
// the `stream` prop) so we don't need a second getUserMedia call.
//
// Renders nothing — pure side-effect component, same shape as
// ProctoringEngine / LockdownEngine.
//
// Props:
//   stream         : MediaStream — already-opened webcam stream from iCode
//   testId         : string
//   applicationId? : number
//   candidateEmail?: string
//   active?        : boolean — pause when false

import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import {
  useCheatingFeatureFlags,
  getCheatingFeatureThreshold,
} from '../Proctoring/useCheatingFeatureFlag';

const SNAPSHOT_ENDPOINT = '/api/cheating/snapshot';
const DEFAULT_INTERVAL_SECONDS = 120;
const JPEG_QUALITY = 0.85;
const MAX_INFLIGHT = 2;  // cap concurrent uploads so a slow connection
                         // doesn't queue up dozens of frames in memory

// Cross-engine event name. The Layer 3 ProctoringEngine emits this when
// any of its detectors fires a non-info event so the snapshot engine
// can grab a tamper-proof server-side frame of the moment.
//
// Schema: detail = { feature_key: string, severity: string }
const CHEATING_EVENT_BUS = 'catapulyst:cheating-detector-fired';


export default function SnapshotEngine({
  stream,
  testId,
  applicationId = null,
  candidateEmail = null,
  active = true,
}) {
  const { flags, loading: flagsLoading } = useCheatingFeatureFlags();
  const videoRef = useRef(null);
  const intervalRef = useRef(null);
  const inflightRef = useRef(0);

  // ── Bind the supplied stream to an offscreen <video> for canvas capture ─
  useEffect(() => {
    if (!stream || !videoRef.current) return undefined;
    try {
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true;
      videoRef.current.playsInline = true;
      const p = videoRef.current.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[snapshots] stream bind failed:', err?.message || err);
    }
    return () => {
      try { if (videoRef.current) videoRef.current.srcObject = null; }
      catch (_) { /* defensive */ }
    };
  }, [stream]);

  // ── Capture-and-POST helper ─────────────────────────────────────────────
  const captureAndUpload = async (reason, triggerFeature) => {
    if (inflightRef.current >= MAX_INFLIGHT) {
      // eslint-disable-next-line no-console
      console.debug('[snapshots] skipping capture — inflight cap reached');
      return;
    }
    if (!videoRef.current || !stream) return;
    const video = videoRef.current;
    if (video.readyState < 2) return;  // metadata not loaded yet

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Do NOT mirror — Rekognition expects natural orientation.
    ctx.drawImage(video, 0, 0, w, h);

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      inflightRef.current += 1;
      try {
        const fd = new FormData();
        fd.append('test_id', testId);
        fd.append('image', blob, 'snapshot.jpg');
        fd.append('reason', reason || 'scheduled');
        if (applicationId !== null && applicationId !== undefined) {
          fd.append('application_id', String(applicationId));
        }
        if (candidateEmail) fd.append('candidate_email', candidateEmail);
        if (triggerFeature) fd.append('trigger_feature', triggerFeature);

        await axios.post(SNAPSHOT_ENDPOINT, fd, { timeout: 45000 });
      } catch (err) {
        // Don't retry — proctoring streams should never queue up. Next
        // scheduled tick or event-trigger will produce a fresh frame.
        // eslint-disable-next-line no-console
        console.warn('[snapshots] upload failed:', err?.message || err);
      } finally {
        inflightRef.current = Math.max(0, inflightRef.current - 1);
      }
    }, 'image/jpeg', JPEG_QUALITY);
  };

  // ── Scheduled-interval capture ──────────────────────────────────────────
  useEffect(() => {
    if (!active || flagsLoading || !testId || !stream) return undefined;
    if (!flags['snapshots.scheduled']?.enabled) return undefined;

    const intervalSeconds = Number(
      (getCheatingFeatureThreshold('snapshots.scheduled') || {}).interval_seconds
    ) || DEFAULT_INTERVAL_SECONDS;
    const intervalMs = Math.max(15, intervalSeconds) * 1000;  // floor at 15s

    // eslint-disable-next-line no-console
    console.info('[snapshots] scheduled captures every', intervalSeconds, 'seconds');

    // First capture immediately so the recruiter has a baseline frame
    // even if the test ends before the first interval lapses.
    void captureAndUpload('scheduled');
    intervalRef.current = setInterval(() => {
      void captureAndUpload('scheduled');
    }, intervalMs);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, flagsLoading, testId, stream, flags?.['snapshots.scheduled']?.enabled]);

  // ── Event-triggered capture ────────────────────────────────────────────
  useEffect(() => {
    if (!active || flagsLoading || !testId || !stream) return undefined;
    if (!flags['snapshots.event_triggered']?.enabled) return undefined;

    // eslint-disable-next-line no-console
    console.info('[snapshots] event-triggered capture enabled');

    const handler = (ev) => {
      try {
        const detail = ev?.detail || {};
        void captureAndUpload('event_triggered', detail.feature_key);
      } catch (_) { /* defensive */ }
    };
    window.addEventListener(CHEATING_EVENT_BUS, handler);
    return () => {
      window.removeEventListener(CHEATING_EVENT_BUS, handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, flagsLoading, testId, stream, flags?.['snapshots.event_triggered']?.enabled]);

  return (
    <video
      ref={videoRef}
      autoPlay muted playsInline
      style={{ display: 'none' }}
      aria-hidden="true"
    />
  );
}


// ── Exported helper so ProctoringEngine / LockdownEngine can fire the
//    event-bus signal in a typed-friendly way without importing internals.
export function fireCheatingDetectorEvent(featureKey, severity) {
  try {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(CHEATING_EVENT_BUS, {
      detail: { feature_key: featureKey, severity },
    }));
  } catch (_) { /* defensive */ }
}
